import type { RJSFSchema } from '@rjsf/utils'
import { Fragment, ReactNode, useId } from 'react'
import { diff } from 'deep-object-diff'
import { payloadWidgets } from '../../schemas/payload-widgets'
import {
  getObjectSubEntries,
  searchSchemaForTitle,
  resolveEnumTitleFromSchema,
  resolveNestedEnumTitleFromSchema
} from '../../schemas/schema-utils'
import { CheckCircleIcon, Mtds } from 'prove-common'
import { toLocaleDateString } from '../../utils/dateUtils'

const ENABLE_TECHNICAL_DETAILS_POPOVER = true

const formatValue = (value: unknown): string => {
  if (value == null) return '-'
  if (typeof value === 'string') return value.trim() === '' ? '-' : value
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value)
  return 'Denne verdien kan ikke vises'
}

const formatForbehandling = (
  schema: RJSFSchema,
  value: Record<string, unknown>
) => {
  const nivaa2 = formatValue(value.nivaa2)
  const nivaa3 = formatValue(value.nivaa3)
  const nivaa2Title = resolveNestedEnumTitleFromSchema(
    schema,
    'forbehandling',
    'nivaa2',
    nivaa2
  )
  const nivaa3Title = resolveNestedEnumTitleFromSchema(
    schema,
    'forbehandling',
    'nivaa3',
    nivaa3
  )

  return [
    nivaa2Title ?? nivaa2,
    nivaa3Title ?? (nivaa3 === '-' ? null : nivaa3)
  ]
    .filter((entry): entry is string => entry != null && entry !== '-')
    .join(' - ')
}

type FlatEntry = {
  kind: 'flat'
  label: string
  value: string
  hasChanged: boolean
  _key: string
}

type GroupEntry = {
  kind: 'group'
  label: string
  subEntries: Array<{ label: string; value: string; _key: string }>
  hasChanged: boolean
  _key: string
}

type Entry = FlatEntry | GroupEntry

const getPayloadEntriesFromRequisition = (
  schema: string,
  currentPayload?: string,
  oldPayload?: string
): Entry[] => {
  let payloadDiff = {}
  if (oldPayload && currentPayload) {
    payloadDiff = diff(JSON.parse(oldPayload), JSON.parse(currentPayload))
  }

  const widgetOrder = payloadWidgets.map((w) => w.propKey)
  const parsedSchema: RJSFSchema = schema ? JSON.parse(schema) : {}
  const parsedPayload: unknown = currentPayload
    ? JSON.parse(currentPayload)
    : {}
  const entries = Object.entries(parsedPayload as Record<string, unknown>)

  const unsortedEntries = entries.flatMap<Entry>(([key, value]) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const title = searchSchemaForTitle(parsedSchema, key)
      if (!title) return []
      if (key === 'forbehandling') {
        return [
          {
            kind: 'flat',
            label: title,
            value: formatForbehandling(
              parsedSchema,
              value as Record<string, unknown>
            ),
            hasChanged: key in payloadDiff,
            _key: key
          }
        ]
      }
      const subEntries = getObjectSubEntries(
        parsedSchema,
        key,
        value as Record<string, unknown>
      ).map((entry) => ({
        label: entry.label,
        value: entry.value,
        _key: entry._key
      }))
      if (subEntries.length === 0) {
        return [
          {
            kind: 'flat' as const,
            label: title,
            value: formatValue(value),
            hasChanged: key in payloadDiff,
            _key: key
          }
        ]
      }
      return [
        {
          kind: 'group',
          label: title,
          subEntries,
          hasChanged: key in payloadDiff,
          _key: key
        }
      ]
    }

    const title = searchSchemaForTitle(parsedSchema, key)
    const valueString = formatValue(value)
    let translatedValue =
      resolveEnumTitleFromSchema(parsedSchema, key, valueString) ?? valueString
    if (key === 'holdbarhetsdato' || key === 'sisteEktoparasittbehandling') {
      translatedValue =
        valueString.length > 0 ? (toLocaleDateString(valueString) ?? '') : ''
    } else if (key === 'vanntemperatur') {
      translatedValue = valueString.length > 0 ? `${valueString} °C` : ''
    }

    if (!title) return []
    return [
      {
        kind: 'flat',
        label: title,
        value: translatedValue,
        hasChanged: key in payloadDiff,
        _key: key
      }
    ]
  })

  return unsortedEntries.toSorted((a, b) => {
    const aIdx = widgetOrder.indexOf(a._key)
    const bIdx = widgetOrder.indexOf(b._key)
    if (aIdx === -1 && bIdx === -1) return 0
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

type FilledOutByInspectorSectionProps = {
  schema: string
  currentPayload?: string
  oldPayload?: string
  /** Compact mode: no heading, simple two-column grid, returns null when there are no rows */
  compact?: boolean
  children?: ReactNode
}

export const FilledOutByInspectorSection = ({
  schema,
  currentPayload,
  oldPayload,
  compact,
  children
}: FilledOutByInspectorSectionProps) => {
  const entries = getPayloadEntriesFromRequisition(
    schema,
    currentPayload,
    oldPayload
  )
  const visibleEntries = ENABLE_TECHNICAL_DETAILS_POPOVER
    ? entries
    : entries.filter((entry) => entry.kind !== 'group')
  const sectionId = useId()

  if (compact) {
    if (visibleEntries.length === 0 && !children) return null
    return (
      <Mtds.Grid data-gap='2'>
        {visibleEntries.length > 0 && (
          <Mtds.Grid
            className='grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm leading-relaxed'
            data-gap={undefined}
          >
            {visibleEntries.map((entry) => {
              if (entry.kind === 'group') {
                const popId = `${sectionId}-${entry._key}`
                return (
                  <Fragment key={entry._key}>
                    <span className='whitespace-nowrap self-center'>
                      {entry.label}
                    </span>
                    <span className='font-semibold'>
                      <button
                        type='button'
                        data-popover='inline'
                        popovertarget={popId}
                      >
                        Vis detaljer
                        {entry.hasChanged && <CheckCircleIcon size={12} />}
                      </button>
                      <Mtds.Popover id={popId} data-placement='bottom-start'>
                        <div className='grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm'>
                          {entry.subEntries.map((sub) => (
                            <Fragment key={sub._key}>
                              <span className='whitespace-nowrap'>
                                {sub.label}
                              </span>
                              <span className='font-semibold'>{sub.value}</span>
                            </Fragment>
                          ))}
                        </div>
                      </Mtds.Popover>
                    </span>
                  </Fragment>
                )
              }
              return (
                <Fragment key={entry._key}>
                  <span className='whitespace-nowrap'>{entry.label}</span>
                  <span className='font-semibold flex items-center gap-2'>
                    {entry.value}
                    {entry.hasChanged && <CheckCircleIcon size={16} />}
                  </span>
                </Fragment>
              )
            })}
          </Mtds.Grid>
        )}
        {children}
      </Mtds.Grid>
    )
  }

  return (
    <Mtds.Grid data-gap='2' data-align-content={'start'}>
      <Mtds.Heading as='h3' data-size='xs'>
        Opplysninger fra skjema
      </Mtds.Heading>
      <Mtds.Table data-fixed>
        <tbody>
          {visibleEntries.length === 0 ? (
            <tr>
              <td colSpan={2}>
                <Mtds.Muted>
                  Ingen opplysninger er fylt ut av inspektør.
                </Mtds.Muted>
              </td>
            </tr>
          ) : (
            visibleEntries.map((entry) => {
              if (entry.kind === 'group') {
                const popId = `${sectionId}-${entry._key}`
                return (
                  <tr key={entry._key}>
                    <th>{entry.label}</th>
                    <td>
                      <button
                        type='button'
                        data-popover='inline'
                        popovertarget={popId}
                      >
                        Vis detaljer
                        {entry.hasChanged && <CheckCircleIcon size={12} />}
                      </button>
                      <Mtds.Popover id={popId} data-placement='bottom-start'>
                        <div className='grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm'>
                          {entry.subEntries.map((sub) => (
                            <Fragment key={sub._key}>
                              <span className='text-text-subtle whitespace-nowrap'>
                                {sub.label}
                              </span>
                              <span>{sub.value}</span>
                            </Fragment>
                          ))}
                        </div>
                      </Mtds.Popover>
                    </td>
                  </tr>
                )
              }
              return (
                <tr key={entry._key}>
                  <th>{entry.label}</th>
                  <td>
                    <Mtds.Flex
                      data-justify={'space-between'}
                      data-align={'center'}
                      className={'break-all'}
                    >
                      {entry.value}
                      {entry.hasChanged && <CheckCircleIcon />}
                    </Mtds.Flex>
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </Mtds.Table>
      {children}
    </Mtds.Grid>
  )
}
